/**
 * How a bus is drawn: one colour, glyph and pulse per marker look, read by the
 * map markers, the legend and the vehicle list alike. The map kept its own
 * colour table once, and drifted: it painted unclaimed, standing-by and
 * on-trip buses all orange while the legend beside it said orange, blue and
 * green.
 */
import type { VehicleLiveState, VehicleLiveStateMeta } from "../api/types";
import { PANIC_MARKER_META, VEHICLE_LIVE_STATE_META } from "../../../shared/types.ts";

/** Every distinct marker on the live map: a lifecycle state, or a panic. */
export type MarkerLook = VehicleLiveState | "panic";

export const BUS_GLYPH =
  "M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4S4 2.5 4 6v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM18 11H6V6h12v5z";

/** An exclamation mark: a panicking bus must not read as one that merely
 *  went quiet, though both are red. */
export const ALARM_GLYPH = "M10 3h4v11h-4z M12 16.5a2.25 2.25 0 1 1 0 4.5a2.25 2.25 0 1 1 0-4.5z";

export interface MarkerLookMeta extends VehicleLiveStateMeta {
  glyph: string;
}

export function markerLookMeta(look: MarkerLook): MarkerLookMeta {
  if (look === "panic") return { ...PANIC_MARKER_META, glyph: ALARM_GLYPH };
  return { ...VEHICLE_LIVE_STATE_META[look], glyph: BUS_GLYPH };
}

/**
 * The legend, grouped the way dispatchers read the map: what needs someone to
 * act, and normal fleet activity. Every look the map can draw has exactly one
 * row — panic included, which the legend used to leave out — and there is no
 * row for anything the map never draws (markerLook.test.ts holds it to that).
 */
export const LEGEND_GROUPS: { heading: string; looks: MarkerLook[] }[] = [
  { heading: "Needs attention", looks: ["panic", "lost_contact", "moving_no_trip", "no_gps", "offline"] },
  { heading: "Normal", looks: ["on_trip", "standing_by", "unclaimed"] },
];
